/**
 * Constants for Vercel AI SDK Chat V2
 */

import type { AgentConfig, AgentId } from './types'

// =============================================================================
// Feature Flag
// =============================================================================

/**
 * Environment variable name for feature flag
 */
export const VERCEL_AI_CHAT_FLAG = 'VITE_USE_VERCEL_AI_CHAT'

// =============================================================================
// API Configuration
// =============================================================================

/**
 * Base path for chat API - using V1 backend (proven, stable)
 * The ChatV2 UI uses frontend adapters to work with V1 endpoints
 */
export const CHAT_V2_API_BASE = '/v1'

/**
 * Endpoints for chat API (V1 backend)
 */
export const CHAT_V2_ENDPOINTS = {
  chat: `${CHAT_V2_API_BASE}/chat`,
  conversations: `${CHAT_V2_API_BASE}/chat/conversations`,
  conversation: (id: string) => `${CHAT_V2_API_BASE}/chat/conversations/${id}`,
  messages: (conversationId: string) =>
    `${CHAT_V2_API_BASE}/chat/conversations/${conversationId}/messages`,
  conversationsByContext: (contextId: string) =>
    `${CHAT_V2_API_BASE}/chat/conversations/by-context/${contextId}`,
} as const

/**
 * Required header for Vercel AI SDK stream protocol
 */
export const VERCEL_AI_STREAM_HEADER = 'x-vercel-ai-ui-message-stream'
export const VERCEL_AI_STREAM_VERSION = 'v1'

/**
 * Intuigence parts protocol version (PR 16 — design Risks
 * §"Vercel SDK version churn"). Versioned independently of
 * Vercel's SDK protocol so a SDK bump that ships a new wire
 * shape does not silently change OUR wire shape.
 *
 * Bumping this number = breaking change to the `data-*` part
 * vocabulary (e.g. removing a required field). Additive changes
 * (new part type, new optional field) do NOT bump. Mirror of
 * `INTUIGENCE_PARTS_VERSION` in `ui-message-stream.ts`.
 */
export const INTUIGENCE_PARTS_HEADER = 'x-intuigence-parts'
export const INTUIGENCE_PARTS_VERSION = 'v1'

// =============================================================================
// Agent Configuration
// =============================================================================

/**
 * Default agent ID
 */
export const DEFAULT_AGENT: AgentId = 'pam'

/**
 * Agent configurations
 */
export const AGENTS: Record<AgentId, AgentConfig> = {
  pam: {
    id: 'pam',
    name: 'PAM',
    label: 'AI Plant Support Engineer',
    description: 'AI Plant Support Engineer',
    color: 'amber',
    mentionName: '@ai-plant-support-engineer',
    avatarUrl: '/images/optimized/plant-support-engineer-avatar.webp',
    isAvailable: true,
  },
  aice: {
    id: 'aice',
    name: 'AICE',
    label: 'AI Chemical Engineer',
    description: 'AI Chemical Engineer',
    color: 'blue',
    mentionName: '@ai-chemical-engineer',
    avatarUrl: '/images/optimized/chemical-agent-avatar.webp',
    isAvailable: true,
  },
  max: {
    id: 'max',
    name: 'Max',
    label: 'AI Mechanical Engineer',
    description: 'AI Mechanical Engineer',
    color: 'green',
    mentionName: '@ai-mechanical-engineer',
    avatarUrl: '/images/optimized/mechanical-agent-avatar.webp',
    isAvailable: true,
  },
  ella: {
    id: 'ella',
    name: 'ELLA',
    label: 'AI Electrical Engineer',
    description: 'AI Electrical Engineer',
    color: 'purple',
    mentionName: '@ai-electrical-engineer',
    avatarUrl: '/images/optimized/electrical-agent-avatar.webp',
    isAvailable: true,
  },
} as const

/**
 * Get all available agents as array
 */
export const AGENTS_LIST: AgentConfig[] = Object.values(AGENTS)

/**
 * Agent color mappings for Tailwind CSS
 */
export const AGENT_COLORS = {
  amber: {
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    bgHover: 'bg-amber-100 dark:bg-amber-900/30',
    text: 'text-amber-700 dark:text-amber-400',
    border: 'border-amber-200 dark:border-amber-800',
    ring: 'ring-amber-500',
    gradient: 'from-amber-400 to-orange-500',
  },
  blue: {
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    bgHover: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-700 dark:text-blue-400',
    border: 'border-blue-200 dark:border-blue-800',
    ring: 'ring-blue-500',
    gradient: 'from-blue-400 to-cyan-500',
  },
  green: {
    bg: 'bg-green-50 dark:bg-green-900/20',
    bgHover: 'bg-green-100 dark:bg-green-900/30',
    text: 'text-green-700 dark:text-green-400',
    border: 'border-green-200 dark:border-green-800',
    ring: 'ring-green-500',
    gradient: 'from-green-400 to-emerald-500',
  },
  purple: {
    bg: 'bg-purple-50 dark:bg-purple-900/20',
    bgHover: 'bg-purple-100 dark:bg-purple-900/30',
    text: 'text-purple-700 dark:text-purple-400',
    border: 'border-purple-200 dark:border-purple-800',
    ring: 'ring-purple-500',
    gradient: 'from-purple-400 to-pink-500',
  },
} as const

// =============================================================================
// Agent Mention Detection
// =============================================================================

/**
 * Regex patterns for detecting agent mentions in messages
 * Uses negative lookbehind (?<!\w) and word boundary \b to ensure proper mention detection
 */
export const AGENT_MENTION_PATTERNS: Record<AgentId, RegExp> = {
  pam: /(?<!\w)@(pam|ai-plant-support-engineer)\b/i,
  aice: /(?<!\w)@(aice|ai-chemical-engineer)\b/i,
  max: /(?<!\w)@(max|ai-mechanical-engineer)\b/i,
  ella: /(?<!\w)@(ella|ai-electrical-engineer)\b/i,
}

/**
 * Combined pattern for detecting any agent mention
 * Global flag (g) for finding all mentions in a message
 */
export const ANY_AGENT_MENTION_PATTERN =
  /(?<!\w)@(pam|aice|max|ella|ai-plant-support-engineer|ai-chemical-engineer|ai-mechanical-engineer|ai-electrical-engineer)\b/gi

/**
 * Single mention pattern (without global flag) for testing
 */
export const SINGLE_AGENT_MENTION_PATTERN =
  /(?<!\w)@(pam|aice|max|ella|ai-plant-support-engineer|ai-chemical-engineer|ai-mechanical-engineer|ai-electrical-engineer)\b/i

// =============================================================================
// Quick Actions
// =============================================================================

/**
 * Quick action button configurations
 */
export const QUICK_ACTIONS = [
  { id: 'calculate', label: 'Calculate', icon: 'calculator' },
  { id: 'show-triggers', label: 'Show triggers', icon: 'alert' },
  { id: 'troubleshoot', label: 'Troubleshoot', icon: 'wrench' },
  { id: 'plan', label: 'Plan', icon: 'clipboard' },
] as const

export type QuickActionId = (typeof QUICK_ACTIONS)[number]['id']

// =============================================================================
// SSE Stream Constants
// =============================================================================

/**
 * SSE stream termination marker
 */
export const SSE_DONE_MARKER = '[DONE]'

/**
 * Default timeout for stream responses (30 seconds)
 */
export const STREAM_TIMEOUT_MS = 30000

// =============================================================================
// UI Constants
// =============================================================================

/**
 * Maximum message length
 */
export const MAX_MESSAGE_LENGTH = 10000

/**
 * Throttle delay for streaming updates (ms)
 */
export const STREAM_THROTTLE_MS = 50

/**
 * Auto-scroll threshold (pixels from bottom)
 */
export const AUTO_SCROLL_THRESHOLD = 100
